import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { djangoApi } from "../api/auth";
import { useExam } from "../context/ExamContext";

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
}

export default function BeginnerSubmitted() {
  const navigate = useNavigate();
  const { attemptId: routeAttemptId } = useParams();

  const { startNewExamFlow, logout } = useExam();

  const [attemptData, setAttemptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let active = true;

    const loadAttempt = async () => {
      setAttemptData(null);
      setLoadError("");

      if (!routeAttemptId) {
        setLoadError(
          "The submission URL does not contain an attempt ID."
        );
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Use the shared Django client and its authenticated JWT.
        const response = await djangoApi.get(
          `/api/beginner/main-exam-attempts/${encodeURIComponent(
            routeAttemptId
          )}/`
        );

        if (!active) return;

        const data = response?.data;

        if (!data || typeof data !== "object") {
          throw new Error(
            "The server returned an invalid exam submission."
          );
        }

        const returnedId = data.id ?? data.attempt_id;

        // Never display a response that cannot be matched to this URL.
        if (
          returnedId == null ||
          String(returnedId) !== String(routeAttemptId)
        ) {
          throw new Error(
            "The server response does not match this exam attempt."
          );
        }

        setAttemptData(data);
      } catch (error) {
        if (!active) return;

        console.error(
          "BEGINNER SUBMISSION LOAD ERROR:",
          error?.response?.data || error
        );

        const statusCode = error?.response?.status;

        let message =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Could not load this submission. Please try again.";

        if (statusCode === 401) {
          message =
            "Your login session may have expired. Please log in again.";
        } else if (statusCode === 403 || statusCode === 404) {
          message =
            "This attempt could not be accessed. Verify that you are logged in with the correct student account and that the attempt belongs to you.";
        }

        setLoadError(message);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadAttempt();

    return () => {
      active = false;
    };
  }, [routeAttemptId]);

  const examName =
    attemptData?.exam_name ||
    attemptData?.exam?.name ||
    attemptData?.main_exam_name ||
    "Beginner Main Exam";

  const courseName =
    attemptData?.course_name ||
    attemptData?.course?.name ||
    "Beginner";

  const submittedAt =
    attemptData?.submitted_at ||
    attemptData?.completed_at ||
    attemptData?.updated_at ||
    null;

  const status = String(
    attemptData?.status ||
      attemptData?.state ||
      "Submission status unavailable"
  );

  const autoSubmitted = Boolean(
    attemptData?.auto_submitted ??
      attemptData?.autoSubmitted ??
      false
  );

  const returnHome = () => {
    startNewExamFlow();
    navigate("/beginner/dashboard", { replace: true });
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="oes-page-full">
        <div className="oes-page container p-4 text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            aria-label="Loading"
          />
          <p className="text-muted mt-3">
            Loading your submission...
          </p>
        </div>
      </div>
    );
  }

  // Do not show a successful submission screen if the
  // authenticated backend request failed.
  if (loadError || !attemptData) {
    return (
      <div className="oes-page-full">
        <div className="oes-page container p-4">
          <div className="d-flex justify-content-end mb-3">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={handleLogout}
            >
              <i className="bi bi-box-arrow-right me-1" />
              Logout
            </button>
          </div>

          <div className="alert alert-danger">
            <h5 className="alert-heading">
              Unable to load submission
            </h5>
            <p className="mb-0">{loadError}</p>
          </div>

          <button
            className="btn btn-primary"
            onClick={returnHome}
          >
            Return to Beginner Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="oes-page-full">
      <div className="oes-home-shell d-flex flex-row overflow-hidden">
        <div className="flex-grow-1 oes-page container">
          <div className="d-flex justify-content-end align-items-center gap-3 mb-3">
            <i className="bi bi-bell fs-5 text-secondary" />

            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={handleLogout}
            >
              <i className="bi bi-box-arrow-right me-1" />
              Logout
            </button>
          </div>

          <div
            className="oes-card p-4 p-md-5 text-center"
            style={{ maxWidth: 560, margin: "0 auto" }}
          >
            <div
              className="rounded-circle bg-success d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: 72, height: 72 }}
            >
              <i className="bi bi-check-lg text-white fs-2" />
            </div>

            <h2 className="h5 fw-bold text-success mb-1">
              Successfully Submitted!
            </h2>

            <p className="text-muted mb-4">
              Your Beginner Main Exam submission has been recorded.
              Your official result will appear when it is published.
            </p>

            <table className="table table-borderless text-start mb-3">
              <tbody>
                <tr>
                  <td className="text-muted small">Course</td>
                  <td>: {courseName}</td>
                </tr>

                <tr>
                  <td className="text-muted small">Examination</td>
                  <td>: {examName}</td>
                </tr>

                <tr>
                  <td className="text-muted small">Attempt</td>
                  <td>: #{routeAttemptId}</td>
                </tr>

                <tr>
                  <td className="text-muted small">
                    Submitted / Updated
                  </td>
                  <td>: {formatDate(submittedAt)}</td>
                </tr>

                <tr>
                  <td className="text-muted small">Status</td>
                  <td>
                    : <span className="fw-semibold">
                      {status}
                    </span>
                  </td>
                </tr>

                {autoSubmitted && (
                  <tr>
                    <td className="text-muted small">Note</td>
                    <td>: Auto-submitted when time expired</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="alert alert-info small text-start">
              Submission details are loaded from the server for
              attempt #{routeAttemptId}. Previous results saved in
              frontend context are not used as a fallback.
            </div>

            <button
              className="btn btn-primary w-100 mt-2"
              onClick={returnHome}
            >
              RETURN TO BEGINNER DASHBOARD
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}